/**
 * BeforeAfterSlider — Drag-to-reveal comparison slider for "avant / après" photos.
 *
 * NON-CORE FILE — additive customization for Les Jardins d'Éole.
 *
 * Activation rules (frontend-only convention, no backend schema change):
 *  - Title contains "[avant-après]" or "[avant/apres]" (case-insensitive)
 *  - OR exactly 2 images in r.images and the first/second alt text hints
 *    "avant" / "après" via shouldUseBeforeAfter() helper.
 */
import React, { useEffect, useRef, useState } from "react";
import { GitCompare, MoveHorizontal } from "lucide-react";

/**
 * Returns the [beforeUrl, afterUrl] tuple if the realization should use the
 * comparison slider, or null otherwise. Strips the marker from the title for
 * display purposes (caller can use stripBeforeAfterMarker(title) too).
 */
export function getBeforeAfterPair(realization) {
  if (!realization) return null;
  const title = (realization.title || "").toLowerCase();
  const hasMarker = /\[avant\s*[-/]\s*apr[èe]s\]/i.test(title);
  const imgs = Array.isArray(realization.images) ? realization.images.filter(Boolean) : [];
  if (hasMarker && imgs.length >= 2) {
    return { before: imgs[0], after: imgs[1] };
  }
  return null;
}

export function stripBeforeAfterMarker(title) {
  if (!title) return title;
  return title.replace(/\s*\[avant\s*[-/]\s*apr[èe]s\]\s*/i, " ").replace(/\s+/g, " ").trim();
}

export default function BeforeAfterSlider({ before, after, beforeLabel = "Avant", afterLabel = "Après", aspectRatio = "16 / 10" }) {
  const containerRef = useRef(null);
  const [pct, setPct] = useState(50);
  const draggingRef = useRef(false);

  const setPctFromX = (clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPct(Math.max(0, Math.min(100, next)));
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setPctFromX(x);
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const onStart = (e) => {
    draggingRef.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setPctFromX(x);
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPct((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 4));
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full select-none rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 cursor-ew-resize bg-black"
      style={{ aspectRatio }}
      onMouseDown={onStart}
      onTouchStart={onStart}
      onKeyDown={onKey}
      tabIndex={0}
      role="slider"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Comparateur avant après"
      data-testid="before-after-slider"
    >
      {/* AFTER (background) */}
      <img
        src={after}
        alt={afterLabel}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        loading="lazy"
      />
      {/* BEFORE (clipped to left side based on pct) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pct}%` }}
      >
        <img
          src={before}
          alt={beforeLabel}
          className="absolute inset-0 h-full w-auto max-w-none object-cover pointer-events-none"
          style={{ width: containerRef.current?.offsetWidth || "100%" }}
          loading="lazy"
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/65 text-white text-xs font-bold uppercase tracking-wider backdrop-blur-md pointer-events-none">
        {beforeLabel}
      </div>
      <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-[var(--jdo-gold,#C99E30)] text-[#0F1812] text-xs font-bold uppercase tracking-wider shadow-lg pointer-events-none">
        {afterLabel}
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-[3px] bg-white shadow-[0_0_24px_rgba(0,0,0,0.5)] pointer-events-none"
        style={{ left: `calc(${pct}% - 1.5px)` }}
      />
      {/* Handle */}
      <button
        type="button"
        aria-label="Déplacer le comparateur"
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-white shadow-2xl flex items-center justify-center ring-2 ring-[var(--jdo-gold,#C99E30)] hover:scale-105 transition-transform"
        style={{ left: `${pct}%` }}
        onMouseDown={(e) => { e.stopPropagation(); onStart(e); }}
        onTouchStart={(e) => { e.stopPropagation(); onStart(e); }}
        data-testid="before-after-handle"
      >
        <MoveHorizontal className="w-6 h-6 text-[var(--jdo-blue-deep,#122947)]" strokeWidth={2.5} />
      </button>

      {/* Hint badge bottom — disappears on first interaction */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur text-[11px] font-bold text-[var(--jdo-blue-deep,#122947)] uppercase tracking-wide pointer-events-none shadow-md">
        <GitCompare className="w-3.5 h-3.5" />
        Glissez pour comparer
      </div>
    </div>
  );
}
